/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { usageResumeStripeSubscription } from "../funcs/usageResumeStripeSubscription.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { StripeSubscription } from "../models/components/stripesubscription.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ResumeStripeSubscriptionRequest,
  ResumeStripeSubscriptionSecurity,
} from "../models/operations/resumestripesubscription.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type ResumeStripeSubscriptionMutationVariables = {
  request?: ResumeStripeSubscriptionRequest | undefined;
  security?: ResumeStripeSubscriptionSecurity | undefined;
  options?: RequestOptions;
};

export type ResumeStripeSubscriptionMutationData = StripeSubscription;

export type ResumeStripeSubscriptionMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * resumeStripeSubscription usage
 *
 * @remarks
 * Remove a scheduled end-of-period cancellation from the organization's Stripe PAYG subscription
 */
export function useResumeStripeSubscriptionMutation(
  options?: MutationHookOptions<
    ResumeStripeSubscriptionMutationData,
    ResumeStripeSubscriptionMutationError,
    ResumeStripeSubscriptionMutationVariables
  >,
): UseMutationResult<
  ResumeStripeSubscriptionMutationData,
  ResumeStripeSubscriptionMutationError,
  ResumeStripeSubscriptionMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildResumeStripeSubscriptionMutation(client, options),
    ...options,
  });
}

export function mutationKeyResumeStripeSubscription(): MutationKey {
  return ["@gram/client", "usage", "resumeStripeSubscription"];
}

export function buildResumeStripeSubscriptionMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: ResumeStripeSubscriptionMutationVariables,
  ) => Promise<ResumeStripeSubscriptionMutationData>;
} {
  return {
    mutationKey: mutationKeyResumeStripeSubscription(),
    mutationFn: function resumeStripeSubscriptionMutationFn({
      request,
      security,
      options,
    }): Promise<ResumeStripeSubscriptionMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(usageResumeStripeSubscription(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
